import React from 'react';
import { Box, keyframes, useTheme } from '@mui/material';

// Keyframes for animations
const pulse = keyframes`
  0% { opacity: 0.8; transform: scale(0.95); }
  50% { opacity: 1; transform: scale(1.05); }
  100% { opacity: 0.8; transform: scale(0.95); }
`;

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const wave = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0); }
`;

const searchPulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const AILoader = ({ message = "Generating Image..." }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        borderRadius: 4,
        p: 4,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 180,
          height: 180,
          mb: 4,
        }}
      >
        {/* Main search icon */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 60,
            height: 60,
            borderRadius: '50%',
            bgcolor: primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: `${pulse} 2s ease-in-out infinite`,
            zIndex: 3,
            boxShadow: `0 0 20px ${primary}80`,
          }}
        >
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
            <path
              d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M21 21L16.65 16.65"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>

        {/* Concentric rings */}
        {[1, 2, 3].map((ring) => (
          <Box
            key={ring}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 60 + ring * 40,
              height: 60 + ring * 40,
              borderRadius: '50%',
              border: `2px solid ${primary}${Math.floor((0.3 / ring) * 255).toString(16).padStart(2, '0')}`,
              animation: `${searchPulse} ${3 + ring}s ease-in-out infinite`,
              animationDelay: `${ring * 0.3}s`,
            }}
          />
        ))}

        {/* Floating data points */}
        {[...Array(8)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: `${primary}${Math.floor((0.7 - i * 0.07) * 255).toString(16).padStart(2, '0')}`,
              transform: `translate(-50%, -50%) translateX(${80 + i * 5}px) rotate(${i * 45}deg)`,
              transformOrigin: '0 0',
              animation: `${rotate} ${8 - i}s linear infinite`,
              boxShadow: `0 0 10px ${primary}80`,
            }}
          />
        ))}
      </Box>

      {/* Message with typing indicator */}
      <Box sx={{ textAlign: 'center' }}>
        <Box
          sx={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: primary,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {message}
          <Box
            sx={{
              ml: 1,
              '& span': {
                display: 'inline-block',
                width: 8,
                height: 8,
                bgcolor: primary,
                borderRadius: '50%',
                animation: `${wave} 1.4s ease-in-out infinite`,
              },
              '& span:nth-of-type(1)': { animationDelay: '0s' },
              '& span:nth-of-type(2)': { animationDelay: '0.2s' },
              '& span:nth-of-type(3)': { animationDelay: '0.4s' },
            }}
          >
            <span></span>
            <span></span>
            <span></span>
          </Box>
        </Box>
        <Box
          sx={{
            color: '#5A7D6E',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Optional: Additional status text */}
        </Box>
      </Box>

      {/* Floating data cards */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: 4,
          '& > div': {
            bgcolor: 'white',
            borderRadius: 2,
            p: 1.5,
            boxShadow: `0 5px 15px ${primary}1A`,
            animation: `${wave} 3s ease-in-out infinite`,
          },
        }}
      >
        <Box sx={{ animationDelay: '0s' }}>
          <Box sx={{ width: 60, height: 40, bgcolor: `${primary}1A`, borderRadius: 1, mb: 1 }} />
          <Box sx={{ width: 40, height: 6, bgcolor: `${primary}33`, borderRadius: 1 }} />
        </Box>
        <Box sx={{ animationDelay: '0.2s' }}>
          <Box sx={{ width: 60, height: 40, bgcolor: `${primary}1A`, borderRadius: 1, mb: 1 }} />
          <Box sx={{ width: 50, height: 6, bgcolor: `${primary}33`, borderRadius: 1 }} />
        </Box>
        <Box sx={{ animationDelay: '0.4s' }}>
          <Box sx={{ width: 60, height: 40, bgcolor: `${primary}1A`, borderRadius: 1, mb: 1 }} />
          <Box sx={{ width: 30, height: 6, bgcolor: `${primary}33`, borderRadius: 1 }} />
        </Box>
      </Box>

      {/* Scanning effect */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, transparent, ${primary}, transparent)`,
          animation: `${keyframes`
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          `} 3s linear infinite`,
        }}
      />
    </Box>
  );
};

export default AILoader;
